import React, { useEffect, useState } from "react";
import AdminCategoryService from "../../../Modules/Services/admin.categories.service";
import API_BASE_URL from "../../../config";
import { AdminModal, AdminFlash } from "../../../Components/Admin/AdminOverlays";

const ManageCategories = () => {
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [flash, setFlash] = useState({ type: "", text: "" });

  const showFlash = (type, text) => {
    setFlash({ type, text });
    if (text) window.setTimeout(() => setFlash({ type: "", text: "" }), 4000);
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const fetchedCategories = await AdminCategoryService.getAllCategories();
      setCategories(fetchedCategories);
    } catch {
      showFlash("error", "Не удалось загрузить категории.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (editingCategory) {
      setName(editingCategory.name || "");
      setSortOrder(editingCategory.sort_order ?? 0);
      setImageFile(null);
    } else {
      setName("");
      setSortOrder(0);
      setImageFile(null);
    }
  }, [editingCategory, modalOpen]);

  const openCreate = () => {
    setEditingCategory(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingCategory(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      showFlash("error", "Введите название категории.");
      return;
    }
    try {
      if (editingCategory) {
        await AdminCategoryService.updateCategory(editingCategory.id, {
          name: trimmed,
          sortOrder: Number(sortOrder) || 0,
          imageFile,
        });
        showFlash("success", "Категория обновлена.");
      } else {
        await AdminCategoryService.createCategory({
          name: trimmed,
          sortOrder: Number(sortOrder) || 0,
          imageFile,
        });
        showFlash("success", "Категория создана.");
      }
      setModalOpen(false);
      setEditingCategory(null);
      fetchCategories();
    } catch {
      showFlash("error", "Не удалось сохранить.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить категорию?")) return;
    try {
      await AdminCategoryService.deleteCategory(id);
      showFlash("success", "Категория удалена.");
      fetchCategories();
    } catch {
      showFlash("error", "Не удалось удалить.");
    }
  };

  const sorted = [...categories].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );

  return (
    <div>
      <h1 className="admin-page-title">Категории</h1>

      {flash.text ? (
        <AdminFlash
          type={flash.type}
          message={flash.text}
          onClose={() => setFlash({ type: "", text: "" })}
        />
      ) : null}

      <div className="admin-toolbar">
        <button type="button" className="btn btn--primary" onClick={openCreate}>
          Создать категорию
        </button>
      </div>

      <div className="admin-table-wrap">
        {loading ? (
          <div className="admin-spinner">Загрузка…</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Порядок</th>
                <th>Картинка</th>
                <th>Название</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((record) => (
                <tr key={record.id}>
                  <td>{record.id}</td>
                  <td>{record.sort_order ?? 0}</td>
                  <td>
                    {record.image_url ? (
                      <img
                        className="admin-thumb"
                        src={`${API_BASE_URL}${record.image_url}`}
                        alt=""
                      />
                    ) : (
                      <span className="admin-muted">—</span>
                    )}
                  </td>
                  <td>{record.name}</td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        type="button"
                        className="btn btn--primary btn--small"
                        onClick={() => openEdit(record)}
                      >
                        Изменить
                      </button>
                      <button
                        type="button"
                        className="btn btn--danger btn--small"
                        onClick={() => handleDelete(record.id)}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminModal
        open={modalOpen}
        title={
          editingCategory ? "Редактировать категорию" : "Новая категория"
        }
        onClose={() => {
          setModalOpen(false);
          setEditingCategory(null);
        }}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setModalOpen(false);
                setEditingCategory(null);
              }}
            >
              Отмена
            </button>
            <button type="button" className="btn btn--primary" onClick={handleSave}>
              Сохранить
            </button>
          </>
        }
      >
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="cat-name">
            Название
          </label>
          <input
            id="cat-name"
            className="admin-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="cat-sort">
            Порядок (больше — выше в списке; 0 — внизу)
          </label>
          <input
            id="cat-sort"
            type="number"
            min={0}
            step={1}
            className="admin-input"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
        </div>
        <div className="admin-field">
          <span className="admin-field__label">Картинка (jpg/png)</span>
          <input
            type="file"
            accept="image/png,image/jpeg"
            className="admin-file"
            onChange={(e) =>
              setImageFile(e.target.files?.[0] || null)
            }
          />
          {editingCategory?.image_url && !imageFile ? (
            <p className="admin-muted" style={{ marginTop: "0.5rem" }}>
              Текущий файл: <code>{editingCategory.image_url}</code>
            </p>
          ) : null}
        </div>
      </AdminModal>
    </div>
  );
};

export default ManageCategories;
